import React from 'react'
import { interpolate } from '../utils/proposalVariables'
import { calculateMonthlyPrice, isAsNeeded, isIncludedWithPlan, getFrequencyDisplayLabel } from '../utils/frequencyConfig'

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0)
}

const formatWholeCurrency = (amount) =>
  '$' + Math.round(Number(amount) || 0).toLocaleString('en-US')

// Short "best for…" copy per tier; explicit bestFor wins, then rationale.
const TIER_BEST_FOR = {
  basic: 'Essential curb appeal',
  standard: 'The balance most sites choose',
  premium: 'Show-ready, every visit',
}

const bestForText = (flavor) =>
  flavor.bestFor || TIER_BEST_FOR[String(flavor.id || '').toLowerCase()] || flavor.rationale || ''

const TieredCell = ({ cell, recommended }) => {
  const cls = 'scope-tier__cell' + (recommended ? ' is-rec' : '')
  if (cell.mode === 'included') {
    return (
      <td className={cls}>
        <div className="scope-tier__check">&#10003;</div>
        <div className="scope-tier__freq">{cell.freq || 'Included'}</div>
        {cell.tag && <div className="scope-tier__tag">{cell.tag}</div>}
      </td>
    )
  }
  if (cell.mode === 'optional') {
    return (
      <td className={cls}>
        <span className="scope-tier__optional">Add-on</span>
      </td>
    )
  }
  return (
    <td className={cls}>
      <div className="scope-tier__off">&ndash;</div>
      <div className="scope-tier__off-label">Not included</div>
    </td>
  )
}

const TieredScope = ({ data, variables }) => {
  const flavors = data?.flavors || []
  const services = data?.services || []
  // A single tier has nothing to compare against: no "Recommended" ribbon or
  // column emphasis, no "I choose" checkboxes — just the plan + signature.
  const single = flavors.length === 1
  const isRec = (flavor) => !single && !!flavor.recommended

  return (
    <div className="proposal-section">
      <div className="scope-tier__head">
        <div>
          <div className="proposal-section__headline">
            {data?.headline || (single ? 'Your Service Plan' : 'Choose Your Service Plan')}
          </div>
          <div className="proposal-section__accent-line" />
        </div>
        <div className="scope-tier__head-note">
          {data?.planNote || (single ? 'Billed monthly. Cancel anytime.' : 'All plans billed monthly. Cancel or upgrade anytime.')}
        </div>
      </div>

      <table className={'scope-tier__table' + (single ? ' scope-tier__table--single' : '')}>
        <thead>
          <tr>
            <th className="scope-tier__svc-col">What&rsquo;s included</th>
            {flavors.map((flavor) => (
              <th key={flavor.id} className={'scope-tier__plan' + (isRec(flavor) ? ' is-rec' : '')}>
                {isRec(flavor) && <div className="scope-tier__ribbon">&#9733; Recommended</div>}
                <div className="scope-tier__plan-inner">
                  {!single && <div className="scope-tier__plan-name">{flavor.name}</div>}
                  {bestForText(flavor) && <div className="scope-tier__plan-bestfor">{bestForText(flavor)}</div>}
                  <div className="scope-tier__plan-price">{formatWholeCurrency(flavor.monthly)}</div>
                  <div className="scope-tier__plan-per">per month</div>
                  <div className="scope-tier__plan-sub">
                    {formatWholeCurrency(flavor.annual)}/yr &middot; {formatWholeCurrency(flavor.startupOneTime)} start-up
                  </div>
                  {data?.taxable && (
                    <div className="scope-tier__plan-sub">+ {formatWholeCurrency(flavor.tax)}/mo est. tax ({data.taxRate}%)</div>
                  )}
                  {(flavor.startupItems || []).length > 0 && (
                    <div className="scope-tier__plan-startup">
                      <div className="scope-tier__plan-startup-title">Start-up includes</div>
                      {flavor.startupItems.map((it) => (
                        <div className="scope-tier__plan-startup-item" key={it.key || it.label}>
                          <span>{it.label}</span>
                          <span className="scope-tier__plan-startup-price">{formatWholeCurrency(it.price)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {services.map((svc, i) => (
            <tr key={i}>
              <td className="scope-tier__svc">
                <div className="scope-tier__svc-name">{svc.name}</div>
                {svc.desc && <div className="scope-tier__svc-desc">{interpolate(svc.desc, variables)}</div>}
              </td>
              {(svc.cells || []).map((cell, j) => (
                <TieredCell key={j} cell={cell} recommended={isRec(flavors[j] || {})} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Printed selection + signature line (acceptance surface for the spread).
          All boxes render empty — the customer marks their own choice. */}
      <div className="scope-tier__select">
        {!single && (
          <>
            <div className="scope-tier__select-label">I choose:</div>
            <div className="scope-tier__select-options">
              {flavors.map((flavor) => (
                <div key={flavor.id} className="scope-tier__select-option">
                  <span className="scope-tier__checkbox" />
                  <span className={'scope-tier__select-name' + (flavor.recommended ? ' is-rec' : '')}>{flavor.name}</span>
                </div>
              ))}
            </div>
          </>
        )}
        {single && <div className="scope-tier__select-options" />}
        <div className="scope-tier__select-sig">
          <div className="scope-tier__sig-field scope-tier__sig-field--sig">
            <div className="scope-tier__sig-line" />
            <div className="scope-tier__sig-label">Signature</div>
          </div>
          <div className="scope-tier__sig-field scope-tier__sig-field--name">
            <div className="scope-tier__sig-line" />
            <div className="scope-tier__sig-label">Printed name</div>
          </div>
          <div className="scope-tier__sig-field scope-tier__sig-field--date">
            <div className="scope-tier__sig-line" />
            <div className="scope-tier__sig-label">Date</div>
          </div>
        </div>
      </div>

      {data?.note && <div className="proposal-scope__notes">{interpolate(data.note, variables)}</div>}
    </div>
  )
}

// Backward-compat: use perVisitPrice if present, fall back to monthlyPrice
const getMonthlyPrice = (svc) => {
  if (svc.perVisitPrice != null && svc.perVisitPrice !== '') {
    return calculateMonthlyPrice(svc.perVisitPrice, svc.frequency)
  }
  return Number(svc.monthlyPrice) || 0
}

// Fallback image map for services that don't have an explicit imageUrl
const DEFAULT_IMAGE_MAP = {
  'basic maintenance': '/build/images/proposals/edging.webp',
  irrigation: '/build/images/proposals/sprinklers.webp',
  trimming: '/build/images/proposals/trimming_close.webp',
  'mowing (if applicable)': '/build/images/proposals/edging.webp',
  mowing: '/build/images/proposals/edging.webp',
  'weed control & pre-emergent': '/build/images/proposals/spray_close.webp',
  'weed control': '/build/images/proposals/spray_close.webp',
  'plant replacement': '/build/images/proposals/tree_trim_close.webp',
  fertilization: '/build/images/proposals/fertilizer_sm.webp',
  'pest control': '/build/images/proposals/pest_control.webp',
}

const getServiceImage = (svc) => {
  if (svc.imageUrl) return svc.imageUrl
  return DEFAULT_IMAGE_MAP[svc.name?.toLowerCase()] || ''
}

const ScopeOfServices = ({ data, variables, renderMode }) => {
  if (data?.flavors && data?.services?.some(svc => Array.isArray(svc.cells))) {
    return <TieredScope data={data} variables={variables} />
  }

  const allServices = data?.services || []
  const visibleServices = allServices.filter(svc => !svc.hidden)
  const showTotal = data?.showAnnualTotal !== false

  // Exclude as-needed services from total
  const monthlyTotal = visibleServices.reduce((sum, svc) => {
    if (isAsNeeded(svc.frequency)) return sum
    return sum + getMonthlyPrice(svc)
  }, 0)

  // When renderMode is 'table-only', skip individual service detail pages
  // (they're rendered separately as fixed pages by ProposalPreview)
  const showDetailPages = renderMode !== 'table-only'

  return (
    <>
      {/* Summary Table */}
      <div className="proposal-section">
        <div className="proposal-section__headline">{data?.headline || 'Scope of Services'}</div>
        <div className="proposal-section__accent-line" />
        <table className="proposal-scope__table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Frequency</th>
              <th>Monthly Price</th>
            </tr>
          </thead>
          <tbody>
            {visibleServices.map((svc, idx) => {
              const asNeeded = isAsNeeded(svc.frequency)
              const included = isIncludedWithPlan(svc.frequency)

              return (
                <tr key={idx}>
                  <td>
                    <div className="proposal-scope__service-name">{svc.name}</div>
                    {svc.shortDescription && (
                      <div className="proposal-scope__service-desc">{interpolate(svc.shortDescription, variables)}</div>
                    )}
                  </td>
                  <td>
                    {getFrequencyDisplayLabel(svc.frequency)}
                    {included === true && (
                      <div className="proposal-scope__inclusion-badge proposal-scope__inclusion-badge--included">
                        Included w/ Mtc Plan
                      </div>
                    )}
                    {included === false && (
                      <div className="proposal-scope__inclusion-badge proposal-scope__inclusion-badge--additional">
                        Additional Costs May Apply
                      </div>
                    )}
                  </td>
                  <td>
                    {asNeeded
                      ? '\u2014'
                      : getMonthlyPrice(svc) > 0
                        ? formatCurrency(getMonthlyPrice(svc))
                        : <span className="proposal-scope__included-price">Included</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
          {showTotal && monthlyTotal > 0 && (
            <tfoot>
              <tr className="proposal-scope__total-row">
                <td colSpan={2}>{data?.annualTotalLabel || 'Total Monthly Maintenance Fee'}</td>
                <td>{formatCurrency(monthlyTotal)}<div style={{ fontSize: 9, fontWeight: 400, color: 'var(--proposal-muted)', marginTop: 2 }}>plus taxes &amp; fees</div></td>
              </tr>
            </tfoot>
          )}
        </table>
        {data?.notes && (
          <div className="proposal-scope__notes">{interpolate(data.notes, variables)}</div>
        )}
      </div>

      {/* Service Detail Pages (only when NOT in table-only mode) */}
      {showDetailPages && visibleServices.filter(svc => svc.description).map((svc, idx) => {
        const img = getServiceImage(svc)
        return (
          <div
            className={`proposal-service-detail${!img ? ' proposal-service-detail--no-image' : ''}${idx % 2 === 1 ? ' proposal-service-detail--alt' : ''}`}
            key={idx}
          >
            {img && (
              <div className="proposal-service-detail__image">
                <img src={img} alt={svc.name} />
              </div>
            )}
            <div className="proposal-service-detail__content">
              <div className="proposal-service-detail__title">{svc.name}</div>
              <div className="proposal-service-detail__accent-line" />
              <div className="proposal-service-detail__body">{interpolate(svc.description, variables)}</div>
            </div>
          </div>
        )
      })}
    </>
  )
}

export default ScopeOfServices
